"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.meciVoiceService = exports.MeciVoiceService = exports.MECI_ICE_SERVERS = void 0;
const RealtimeService_1 = require("./RealtimeService");
const AudioService_1 = require("./AudioService");
const KrusAudioProcessor_1 = require("./KrusAudioProcessor");
const KrispAudioProcessor_1 = require("./KrispAudioProcessor");
const RNNoiseAudioProcessor_1 = require("./RNNoiseAudioProcessor");
const realtimeConfig_1 = require("../config/realtimeConfig");
const VoiceNetworkMonitor_1 = require("./VoiceNetworkMonitor");
exports.MECI_ICE_SERVERS = {
    iceServers: [
        { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302', 'stun:stun2.l.google.com:19302'] },
        { urls: ['stun:mail.teysas.com:3478', 'stun:193.111.125.190:3478'] },
        {
            urls: [
                'turn:mail.teysas.com:3478?transport=udp',
                'turn:mail.teysas.com:3478?transport=tcp',
                'turn:193.111.125.190:3478?transport=udp',
                'turn:193.111.125.190:3478?transport=tcp',
            ],
            username: 'meci_user',
            credential: 'meci_turn_secret_2026',
        },
    ],
    iceCandidatePoolSize: 10,
};
class MeciVoiceService {
    constructor() {
        this.channelId = null;
        this.uid = null;
        this.currentUserId = null;
        this.displayName = '';
        this.avatar = '';
        this.isMuted = false;
        this.isDeafened = false;
        this.isVideoEnabled = false;
        this.isScreenSharing = false;
        this.localAudioStream = null;
        this.rawMicStream = null;
        this.localVideoStream = null;
        this.localScreenStream = null;
        // Screen audio senders (socketId -> RTCRtpSender)
        this.screenAudioSenders = new Map();
        // PeerConnections: socketId -> RTCPeerConnection
        this.peerConnections = new Map();
        // socketId -> peer metadata
        this.peerMetadata = new Map();
        // Microphone audio: socketId -> HTMLAudioElement / Web Audio Gain (0-200% volume for user speech)
        this.remoteAudioElements = new Map();
        this.remoteAudioContexts = new Map();
        this.remoteGainNodes = new Map();
        this.remoteVolumes = new Map();
        // Screen / Broadcast audio: socketId -> HTMLAudioElement / Web Audio Gain (0-200% volume for system/screen sound)
        this.remoteStreamAudioElements = new Map();
        this.remoteStreamAudioContexts = new Map();
        this.remoteStreamGainNodes = new Map();
        this.remoteStreamVolumes = new Map();
        this.remoteStreamMuted = new Map();
        // socketId -> remote video tracks
        this.remoteVideoTracks = new Map();
        // Audio Context for local VAD (Voice Activity Detection)
        this.audioContext = null;
        this.analyser = null;
        this.vadInterval = null;
        this.isCurrentlySpeaking = false;
        this.listeners = new Map();
        this.socketListenersAttached = false;
        this.manualQuality = (0, realtimeConfig_1.getVideoQualitySelection)();
        this.netMonitor = new VoiceNetworkMonitor_1.VoiceNetworkMonitor({
            getPeers: () => this.peerConnections,
            getMeta: (sid) => this.peerMetadata.get(sid),
            isCameraOnlyVideo: () => this.isVideoEnabled && !this.isScreenSharing,
            emit: (event, payload) => this.emit(event, payload),
        });
        this.setupSocketListeners();
        if (typeof window !== 'undefined') {
            window.meciVoice = this;
            window.meciDebug = () => this.diagnose();
        }
    }
    // ── Aktarım modu: auto / p2p (sunucusuz) / relay (TURN) ─────────────
    buildRtcConfig() {
        const mode = (0, realtimeConfig_1.getVoiceTransportMode)();
        const base = exports.MECI_ICE_SERVERS.iceServers || [];
        const stunOnly = base.filter((s) => {
            const urls = Array.isArray(s.urls) ? s.urls : [s.urls];
            return urls.every((u) => String(u).startsWith('stun:'));
        });
        const cfg = {
            ...exports.MECI_ICE_SERVERS,
            bundlePolicy: 'max-bundle',
            rtcpMuxPolicy: 'require',
        };
        if (mode === 'p2p') {
            cfg.iceServers = stunOnly;
            cfg.iceTransportPolicy = 'all';
        }
        else if (mode === 'relay') {
            cfg.iceServers = base;
            cfg.iceTransportPolicy = 'relay';
        }
        else {
            cfg.iceServers = base;
            cfg.iceTransportPolicy = 'all';
        }
        return cfg;
    }
    getNetworkQuality() {
        return this.netMonitor.getLast();
    }
    /** Admin paneli / DevTools için canlı anlık görüntü. */
    getLiveSnapshot() {
        const net = this.netMonitor.getLast();
        const peers = Array.from(this.peerMetadata.entries()).map(([sid, p]) => {
            const pc = this.peerConnections.get(sid);
            const st = net?.peers.find((x) => x.socketId === sid);
            const audioEl = this.remoteAudioElements.get(sid);
            return {
                socketId: sid,
                userId: p.userId,
                displayName: p.displayName,
                muted: !!p.muted,
                deafened: !!p.deafened,
                camera: !!p.cameraEnabled,
                screen: !!p.screenSharing,
                pcState: pc?.connectionState || 'none',
                iceState: pc?.iceConnectionState || 'none',
                route: st?.route || 'unknown',
                rttMs: st?.rttMs ?? null,
                lossPct: st?.lossPct ?? 0,
                jitterMs: st?.jitterMs ?? 0,
                inKbps: st?.inKbps ?? 0,
                outKbps: st?.outKbps ?? 0,
                hasAudio: !!audioEl,
                audioPlaying: audioEl ? !audioEl.paused : false,
                volumePct: audioEl ? Math.round(audioEl.volume * 100) : 0,
                hasVideo: !!this.remoteVideoTracks.get(sid) || !!this.remoteVideoTracks.get(String(p.userId)),
            };
        });
        return {
            connected: !!this.channelId,
            channelId: this.channelId,
            serverUrl: (0, realtimeConfig_1.getRealtimeServerUrl)(),
            socketId: RealtimeService_1.realtimeService.getSocket().id || null,
            transportMode: (0, realtimeConfig_1.getVoiceTransportMode)(),
            local: {
                userId: this.currentUserId || this.uid,
                displayName: this.displayName,
                muted: this.isMuted,
                deafened: this.isDeafened,
                camera: this.isVideoEnabled,
                screen: this.isScreenSharing,
            },
            network: net,
            peers,
        };
    }
    // ── Dev Console Live Diagnostics & Telemetry ──────────────────
    status() {
        return this.diagnose();
    }
    getPeerMeta(userId) {
        const targetStr = String(userId).trim();
        for (const [sId, peer] of this.peerMetadata.entries()) {
            if (String(peer.userId) === targetStr || sId === targetStr || `voice-${peer.userId}` === targetStr) {
                return peer;
            }
        }
        return undefined;
    }
    async renegotiateAll() {
        console.log('%c[MeciVoice 🔄] Force renegotiating all active peer connections...', 'color: #38bdf8; font-weight: bold;');
        for (const [remoteSocketId, pc] of this.peerConnections.entries()) {
            try {
                const offer = await pc.createOffer({ iceRestart: true });
                await pc.setLocalDescription(offer);
                RealtimeService_1.realtimeService.getSocket().emit('voice:signal', {
                    toSocketId: remoteSocketId,
                    signal: offer,
                    type: 'offer',
                });
            }
            catch (e) {
                console.warn('[MeciVoice] renegotiateAll error for', remoteSocketId, e);
            }
        }
        this.reattachVideoAll();
    }
    reattachVideoAll() {
        console.log('%c[MeciVoice 🎬] Scanning and reattaching all video surfaces in DOM...', 'color: #10b981; font-weight: bold;');
        document.querySelectorAll('.voice-hero-video, .voice-participant-video').forEach((el) => {
            const elId = el.id || '';
            const parts = elId.split('-');
            const uid = parts[parts.length - 1];
            if (uid) {
                this.attachVideo(uid, el);
            }
        });
    }
    diagnose() {
        console.log('%c================================================================================', 'color: #5865f2; font-weight: bold;');
        console.log('%c🚀 [MECI WEBRTC LIVE DIAGNOSTICS & TELEMETRY] 🚀', 'color: #10b981; font-size: 14px; font-weight: 800;');
        console.log('%c================================================================================', 'color: #5865f2; font-weight: bold;');
        console.log(`%cChannel ID:       %c${this.channelId || 'NOT CONNECTED'}`, 'color: #94a3b8; font-weight: 600;', 'color: #38bdf8; font-weight: bold;');
        console.log(`%cVoice Server URL: %c${(0, realtimeConfig_1.getRealtimeServerUrl)()}`, 'color: #94a3b8; font-weight: 600;', 'color: #f59e0b; font-weight: bold;');
        console.log(`%cSocket ID:        %c${RealtimeService_1.realtimeService.getSocket().id || 'DISCONNECTED'}`, 'color: #94a3b8; font-weight: 600;', 'color: #a855f7; font-weight: bold;');
        console.log(`%cLocal User ID:    %c${this.currentUserId || this.uid}`, 'color: #94a3b8; font-weight: 600;', 'color: #fff; font-weight: bold;');
        console.log(`%cScreen Sharing:   %c${this.isScreenSharing ? 'ACTIVE (Broadcasted)' : 'INACTIVE'}`, 'color: #94a3b8; font-weight: 600;', this.isScreenSharing ? 'color: #22c55e; font-weight: bold;' : 'color: #64748b;');
        console.log(`%cCamera Video:     %c${this.isVideoEnabled ? 'ACTIVE (Broadcasted)' : 'INACTIVE'}`, 'color: #94a3b8; font-weight: 600;', this.isVideoEnabled ? 'color: #22c55e; font-weight: bold;' : 'color: #64748b;');
        console.log(`%cMicrophone Muted: %c${this.isMuted ? 'MUTED' : 'UNMUTED'}`, 'color: #94a3b8; font-weight: 600;', this.isMuted ? 'color: #ef4444; font-weight: bold;' : 'color: #22c55e; font-weight: bold;');
        const nq = this.netMonitor.getLast();
        console.log(`%cTransport Mode:   %c${(0, realtimeConfig_1.getVoiceTransportMode)().toUpperCase()}`, 'color: #94a3b8; font-weight: 600;', 'color: #38bdf8; font-weight: bold;');
        if (nq) {
            console.log(`%cNetwork Quality:  %c${nq.level.toUpperCase()} | RTT ${nq.rttMs}ms | Loss ${nq.lossPct}% | Jitter ${nq.jitterMs}ms | ↓${nq.inKbps}kbps ↑${nq.outKbps}kbps | Route ${nq.route}`, 'color: #94a3b8; font-weight: 600;', 'color: #f59e0b; font-weight: bold;');
        }
        const participantsList = [];
        participantsList.push({
            Role: 'Siz (Local)',
            UserId: this.currentUserId || this.uid,
            DisplayName: this.displayName,
            SocketId: RealtimeService_1.realtimeService.getSocket().id,
            Muted: this.isMuted,
            Camera: this.isVideoEnabled,
            ScreenShare: this.isScreenSharing,
            Tracks: `${this.localAudioStream ? 'Audio' : '-'} | ${this.localScreenStream ? 'Screen' : this.localVideoStream ? 'Camera' : '-'}`,
        });
        for (const [sId, peer] of this.peerMetadata.entries()) {
            const pc = this.peerConnections.get(sId);
            const hasAudio = !!this.remoteAudioElements.get(sId);
            const hasVideo = !!this.remoteVideoTracks.get(sId) || !!this.remoteVideoTracks.get(String(peer.userId));
            participantsList.push({
                Role: 'Remote Peer',
                UserId: peer.userId,
                DisplayName: peer.displayName,
                SocketId: sId,
                Muted: peer.muted,
                Camera: peer.cameraEnabled,
                ScreenShare: peer.screenSharing,
                PCState: pc ? pc.connectionState : 'None',
                Tracks: `${hasAudio ? 'Audio (OK)' : 'No Audio'} | ${hasVideo ? 'Video (OK)' : 'No Video'}`,
            });
        }
        console.log('\n%c👥 CANLI KATILIMCI TABLOSU:', 'color: #f43f5e; font-weight: bold; font-size: 12px;');
        console.table(participantsList);
        console.log('\n%c📡 WEBRTC PEER CONNECTION & TRACK DETAYLARI:', 'color: #8b5cf6; font-weight: bold; font-size: 12px;');
        if (this.peerConnections.size === 0) {
            console.log('   (Henüz bağlı uzak peer yok)');
        }
        else {
            for (const [sId, pc] of this.peerConnections.entries()) {
                const peer = this.peerMetadata.get(sId);
                console.group(`Peer: ${peer?.displayName || 'Unknown'} (Socket: ${sId}, User: ${peer?.userId})`);
                console.log(`Connection State: %c${pc.connectionState}`, 'font-weight: bold; color: #38bdf8');
                console.log(`ICE Connection State: %c${pc.iceConnectionState}`, 'font-weight: bold; color: #38bdf8');
                console.log(`Signaling State: %c${pc.signalingState}`, 'font-weight: bold; color: #38bdf8');
                const senders = pc.getSenders().map((s) => ({
                    kind: s.track?.kind || 'none',
                    id: s.track?.id || 'null',
                    readyState: s.track?.readyState || 'null',
                    muted: s.track?.muted,
                    enabled: s.track?.enabled,
                }));
                console.log('Outbound Senders (Gönderilen):', senders);
                const receivers = pc.getReceivers().map((r) => ({
                    kind: r.track?.kind || 'none',
                    id: r.track?.id || 'null',
                    readyState: r.track?.readyState || 'null',
                    muted: r.track?.muted,
                    enabled: r.track?.enabled,
                }));
                console.log('Inbound Receivers (Alınan):', receivers);
                console.groupEnd();
            }
        }
        console.log('\n%c🔊 SES AKIŞLARI (AUDIO PLAYBACK):', 'color: #06b6d4; font-weight: bold; font-size: 12px;');
        for (const [sId, audioEl] of this.remoteAudioElements.entries()) {
            const peer = this.peerMetadata.get(sId);
            console.log(`- ${peer?.displayName || sId}: Playing=${!audioEl.paused}, Volume=${Math.round(audioEl.volume * 100)}%, Muted=${audioEl.muted}`);
        }
        console.log('\n%c🛠️ DEV CONSOLE HIZLI KOMUTLAR:', 'color: #eab308; font-weight: bold; font-size: 12px;');
        console.log('👉 meciVoice.diagnose()        -> Bu tanılama raporunu tekrar yazdırır');
        console.log('👉 meciVoice.renegotiateAll()   -> Tüm WebRTC video/ses kanallarını zorla yeniler');
        console.log('👉 meciVoice.reattachVideoAll() -> Ekrandaki video elemanlarını yeniden bağlar');
        console.log('%c================================================================================', 'color: #5865f2; font-weight: bold;');
        return {
            channelId: this.channelId,
            socketId: RealtimeService_1.realtimeService.getSocket().id,
            participants: participantsList,
            connections: Array.from(this.peerConnections.keys()),
            remoteVideoTracks: Array.from(this.remoteVideoTracks.keys()),
        };
    }
    // ── Event Emitter ──────────────────────────────────────────────
    on(event, listener) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(listener);
    }
    off(event, listener) {
        this.listeners.get(event)?.delete(listener);
    }
    removeAllListeners() {
        this.listeners.clear();
    }
    emit(event, payload) {
        this.listeners.get(event)?.forEach((l) => {
            try {
                l(payload);
            }
            catch (err) {
                console.error(`[MeciVoiceService] Error in listener for ${event}:`, err);
            }
        });
    }
    // ── Helper to check if a UID string refers to the local user ────
    isLocalUser(uidStr) {
        if (!uidStr)
            return false;
        const str = String(uidStr).trim().toLowerCase();
        if (str === 'local' || str === 'me' || str === 'self')
            return true;
        if (this.uid && String(this.uid).toLowerCase() === str)
            return true;
        if (this.currentUserId && String(this.currentUserId).toLowerCase() === str)
            return true;
        try {
            const storeUser = window.__meci_current_user_id;
            if (storeUser && String(storeUser).toLowerCase() === str)
                return true;
        }
        catch { }
        return false;
    }
    // ── Video Track Wrapper (Agora-compatible for UI) ───────────────
    createVideoTrackWrapper(track, isLocal = false) {
        return {
            play: (containerOrId) => {
                const container = typeof containerOrId === 'string'
                    ? document.getElementById(containerOrId)
                    : containerOrId;
                if (!container)
                    return;
                let videoEl = container.querySelector('video');
                if (!videoEl) {
                    videoEl = document.createElement('video');
                    videoEl.autoplay = true;
                    videoEl.playsInline = true;
                    videoEl.setAttribute('playsinline', 'true');
                    videoEl.setAttribute('webkit-playsinline', 'true');
                    videoEl.muted = true;
                    videoEl.setAttribute('muted', 'true');
                    videoEl.style.width = '100%';
                    videoEl.style.height = '100%';
                    videoEl.style.objectFit = 'contain';
                    videoEl.style.borderRadius = 'inherit';
                    videoEl.style.backgroundColor = '#000';
                    container.appendChild(videoEl);
                }
                else {
                    videoEl.muted = true;
                    videoEl.setAttribute('muted', 'true');
                }
                if (isLocal && !this.localScreenStream) {
                    const mirror = localStorage.getItem('meci_camera_mirror') || 'mirror';
                    const filter = localStorage.getItem('meci_camera_filter') || 'normal';
                    let transform = 'scaleX(-1)';
                    if (mirror === 'normal')
                        transform = 'scaleX(1)';
                    else if (mirror === 'flip')
                        transform = 'scale(-1, -1)';
                    else if (mirror === 'flip_v')
                        transform = 'scaleY(-1)';
                    let filterCss = 'none';
                    if (filter === 'bw')
                        filterCss = 'grayscale(100%) contrast(125%) brightness(105%)';
                    else if (filter === 'warm')
                        filterCss = 'sepia(45%) saturate(145%) hue-rotate(-15deg) contrast(108%)';
                    else if (filter === 'cool')
                        filterCss = 'saturate(115%) hue-rotate(180deg) contrast(112%) brightness(104%)';
                    else if (filter === 'vibrant')
                        filterCss = 'contrast(125%) saturate(145%) brightness(105%)';
                    else if (filter === 'sepia')
                        filterCss = 'sepia(75%) contrast(95%) brightness(105%)';
                    videoEl.style.transform = transform;
                    videoEl.style.filter = filterCss;
                    videoEl.style.transition = 'filter 0.3s ease, transform 0.25s ease';
                }
                const currentStream = videoEl.srcObject;
                if (!currentStream || currentStream.getTracks()[0] !== track) {
                    videoEl.srcObject = new MediaStream([track]);
                }
                const playPromise = videoEl.play();
                if (playPromise !== undefined) {
                    playPromise.catch((err) => {
                        // Ignore AbortError if element was detached from DOM by React
                        if (!videoEl || !videoEl.isConnected)
                            return;
                        console.warn('[MeciVoice] video.play() retry with muted:', err);
                        videoEl.muted = true;
                        videoEl.play().catch(() => { });
                    });
                }
            },
            stop: () => { },
        };
    }
    // ── Track Retrieval & DOM Attachment ────────────────────────────
    getVideoTrack(uidStr) {
        if (this.isLocalUser(uidStr)) {
            const activeStream = this.localScreenStream || this.localVideoStream;
            const track = activeStream?.getVideoTracks()[0];
            if (track) {
                return this.createVideoTrackWrapper(track, true);
            }
            return undefined;
        }
        const targetStr = String(uidStr).trim();
        for (const [socketId, peer] of this.peerMetadata.entries()) {
            if (String(peer.userId) === targetStr ||
                socketId === targetStr ||
                `voice-${peer.userId}` === targetStr) {
                const track = this.remoteVideoTracks.get(socketId) || this.remoteVideoTracks.get(String(peer.userId));
                if (track && track.readyState === 'live') {
                    return this.createVideoTrackWrapper(track, false);
                }
            }
        }
        const fallbackTrack = this.remoteVideoTracks.get(targetStr);
        if (fallbackTrack && fallbackTrack.readyState === 'live') {
            return this.createVideoTrackWrapper(fallbackTrack, false);
        }
        // Fallback: If only 1 remote video track exists in map, bind it
        for (const track of this.remoteVideoTracks.values()) {
            if (track && track.readyState === 'live') {
                return this.createVideoTrackWrapper(track, false);
            }
        }
        // Fallback: scan RTCPeerConnection receivers directly
        for (const [sId, pc] of this.peerConnections.entries()) {
            const peer = this.peerMetadata.get(sId);
            if (!peer || String(peer.userId) === targetStr || sId === targetStr) {
                for (const r of pc.getReceivers()) {
                    if (r.track && r.track.kind === 'video' && r.track.readyState === 'live') {
                        this.remoteVideoTracks.set(sId, r.track);
                        return this.createVideoTrackWrapper(r.track, false);
                    }
                }
            }
        }
        return undefined;
    }
    attachVideo(uidStr, element) {
        try {
            const trackWrapper = this.getVideoTrack(uidStr);
            if (trackWrapper && element) {
                trackWrapper.play(element);
            }
        }
        catch (err) {
            console.warn('[MeciVoice] attachVideo failed for', uidStr, err);
        }
    }
    // ── Socket Events Setup ─────────────────────────────────────────
    setupSocketListeners() {
        if (this.socketListenersAttached)
            return;
        this.socketListenersAttached = true;
        const socket = RealtimeService_1.realtimeService.getSocket();
        socket.on('voice:existing-peers', async ({ channelId, peers }) => {
            if (channelId !== this.channelId)
                return;
            console.log(`[MeciVoice] Received ${peers.length} existing peers in channel ${channelId}`);
            for (const peer of peers) {
                this.peerMetadata.set(peer.socketId, peer);
                this.emit('user-joined', { user: { uid: peer.userId, ...peer } });
                if (peer.cameraEnabled || peer.screenSharing) {
                    this.emit('user-published', {
                        user: { uid: peer.userId },
                        mediaType: 'video',
                        videoTrack: {
                            play: (container) => this.attachVideo(peer.userId, typeof container === 'string' ? document.getElementById(container) : container),
                            stop: () => { },
                        },
                    });
                }
                // As newcomer, we initiate WebRTC offer to existing peers
                await this.createPeerConnection(peer.socketId, true);
            }
        });
        socket.on('voice:user-joined', (peer) => {
            console.log(`[MeciVoice] Remote user joined: ${peer.userId} (${peer.socketId})`);
            this.peerMetadata.set(peer.socketId, peer);
            AudioService_1.audioService.playJoinVoice();
            this.emit('user-joined', { user: { uid: peer.userId, ...peer } });
        });
        socket.on('voice:signal', async ({ fromSocketId, signal, type }) => {
            if (type === 'admin-moderation' && signal) {
                console.log('[MeciVoice 🛡️] Admin moderation command received:', signal);
                try {
                    if (signal.action === 'mute') {
                        await this.toggleMute(!!signal.value);
                    }
                    else if (signal.action === 'deafen') {
                        await this.toggleDeafen(!!signal.value);
                    }
                    else if (signal.action === 'kick') {
                        await this.leaveChannel();
                    }
                }
                catch (e) {
                    console.warn('[MeciVoice] Error executing admin moderation:', e);
                }
                return;
            }
            let pc = this.peerConnections.get(fromSocketId);
            if (type === 'offer') {
                if (!pc) {
                    pc = await this.createPeerConnection(fromSocketId, false);
                }
                try {
                    if (pc.signalingState !== 'stable' && pc.signalingState !== 'have-local-offer') {
                        await Promise.all([
                            pc.setLocalDescription({ type: 'rollback' }).catch(() => { }),
                        ]);
                    }
                    await pc.setRemoteDescription(new RTCSessionDescription(signal));
                    const answer = await pc.createAnswer();
                    await pc.setLocalDescription(answer);
                    socket.emit('voice:signal', {
                        toSocketId: fromSocketId,
                        signal: answer,
                        type: 'answer',
                    });
                }
                catch (err) {
                    console.warn('[MeciVoice] Offer handling error safely bypassed:', err.message);
                }
            }
            else if (type === 'answer') {
                if (pc && pc.signalingState === 'have-local-offer') {
                    try {
                        await pc.setRemoteDescription(new RTCSessionDescription(signal));
                    }
                    catch (err) {
                        console.warn('[MeciVoice] Answer handling error safely bypassed:', err.message);
                    }
                }
            }
            else if (type === 'candidate') {
                if (pc && signal) {
                    try {
                        await pc.addIceCandidate(new RTCIceCandidate(signal));
                    }
                    catch (e) {
                        console.warn('[MeciVoice] Error adding ICE candidate:', e);
                    }
                }
            }
        });
        socket.on('voice:renegotiate', async ({ fromSocketId }) => {
            const pc = this.peerConnections.get(fromSocketId);
            if (pc) {
                try {
                    const offer = await pc.createOffer();
                    await pc.setLocalDescription(offer);
                    socket.emit('voice:signal', {
                        toSocketId: fromSocketId,
                        signal: offer,
                        type: 'offer',
                    });
                }
                catch (e) {
                    console.warn('[MeciVoice] Renegotiation offer creation failed:', e);
                }
            }
        });
        socket.on('voice:state-change', ({ socketId, userId, muted, deafened, cameraEnabled, screenSharing }) => {
            console.log(`[MeciVoice] Remote state change for ${userId}:`, { muted, deafened, cameraEnabled, screenSharing });
            const peer = this.peerMetadata.get(socketId);
            if (peer) {
                if (typeof muted === 'boolean' && peer.muted !== muted) {
                    peer.muted = muted;
                    muted ? AudioService_1.audioService.playMute() : AudioService_1.audioService.playUnmute();
                }
                if (typeof deafened === 'boolean' && peer.deafened !== deafened) {
                    peer.deafened = deafened;
                    deafened ? AudioService_1.audioService.playDeafen() : AudioService_1.audioService.playUndeafen();
                }
                if (typeof cameraEnabled === 'boolean')
                    peer.cameraEnabled = cameraEnabled;
                if (typeof screenSharing === 'boolean')
                    peer.screenSharing = screenSharing;
            }
            // Notify UI store immediately of any state changes
            this.emit('peer-state-change', {
                userId,
                muted,
                deafened,
                cameraEnabled,
                screenSharing,
            });
            if (typeof cameraEnabled === 'boolean' || typeof screenSharing === 'boolean') {
                const hasVideo = !!cameraEnabled || !!screenSharing;
                if (!hasVideo) {
                    this.emit('user-unpublished', { uid: userId, mediaType: 'video' });
                }
                else {
                    const track = this.remoteVideoTracks.get(socketId);
                    if (track) {
                        this.emit('user-published', {
                            user: { uid: userId },
                            mediaType: 'video',
                            videoTrack: this.createVideoTrackWrapper(track),
                        });
                    }
                }
            }
        });
        socket.on('voice:speaking', ({ userId, speaking }) => {
            this.emit('volume-indicator', { uid: userId, level: speaking ? 15 : 0 });
        });
        socket.on('voice:user-left', ({ socketId, userId }) => {
            console.log(`[MeciVoice] Remote user left: ${userId} (${socketId})`);
            this.closePeer(socketId);
            this.peerMetadata.delete(socketId);
            AudioService_1.audioService.playLeaveVoice();
            this.emit('user-left', { uid: userId });
        });
        socket.on('voice:force-mute', async ({ muted, userId }) => {
            if (!userId || String(userId) === String(this.currentUserId || this.uid)) {
                console.log('[MeciVoice 🛡️] Force mute received from admin:', muted);
                await this.toggleMute(!!muted);
            }
        });
        socket.on('voice:force-deafen', async ({ deafened, userId }) => {
            if (!userId || String(userId) === String(this.currentUserId || this.uid)) {
                console.log('[MeciVoice 🛡️] Force deafen received from admin:', deafened);
                await this.toggleDeafen(!!deafened);
            }
        });
        socket.on('voice:force-kick', async ({ channelId }) => {
            if (!channelId || channelId === this.channelId) {
                console.log('[MeciVoice 🛡️] Force kick received from admin');
                await this.leaveChannel();
            }
        });
    }
    // ── WebRTC PeerConnection Management ───────────────────────────
    async createPeerConnection(remoteSocketId, isInitiator) {
        const pc = new RTCPeerConnection(this.buildRtcConfig());
        // Her local SDP'ye Opus FEC/DTX/bitrate ayarı uygula (offer/answer/renegotiate hepsi)
        const origSetLocal = pc.setLocalDescription.bind(pc);
        pc.setLocalDescription = (desc) => {
            if (desc && desc.sdp) {
                const tuned = (0, VoiceNetworkMonitor_1.tuneOpusSdp)(desc.sdp);
                try {
                    // Aynı nesneyi güncelle ki karşıya giden SDP de aynı ayarları taşısın
                    desc.sdp = tuned;
                }
                catch { }
                return origSetLocal({ type: desc.type, sdp: tuned });
            }
            return origSetLocal(desc);
        };
        this.peerConnections.set(remoteSocketId, pc);
        // Audio Transceiver / Track (Mikrofon akışı - her zaman saf konuşma sesi)
        if (this.localAudioStream && this.localAudioStream.getAudioTracks().length > 0) {
            this.localAudioStream.getTracks().forEach((track) => {
                pc.addTrack(track, this.localAudioStream);
            });
        }
        else {
            pc.addTransceiver('audio', { direction: 'sendrecv' });
        }
        // Video Transceiver / Track (Kamera veya Ekran Paylaşımı)
        const activeVideoStream = this.localScreenStream || this.localVideoStream;
        if (activeVideoStream) {
            activeVideoStream.getTracks().forEach((track) => {
                pc.addTrack(track, activeVideoStream);
            });
        }
        else {
            pc.addTransceiver('video', { direction: 'sendrecv' });
        }
        // Eğer o sırada ekran sesi paylaşılıyorsa, ikinci bağımsız ses kanalı olarak ekle
        if (this.isScreenSharing && this.localScreenStream && this.localScreenStream.getAudioTracks().length > 0) {
            const screenAudioTrack = this.localScreenStream.getAudioTracks()[0];
            try {
                screenAudioTrack.contentHint = 'music';
                const sender = pc.addTrack(screenAudioTrack, this.localScreenStream);
                this.screenAudioSenders.set(remoteSocketId, sender);
                console.log(`[MeciVoice 📺🔊] Attached screen audio track to peer ${remoteSocketId}`);
            }
            catch (e) {
                console.warn('[MeciVoice] addTrack for screen audio on new peer failed:', e);
            }
        }
        pc.onicecandidate = (event) => {
            if (event.candidate) {
                RealtimeService_1.realtimeService.getSocket().emit('voice:signal', {
                    toSocketId: remoteSocketId,
                    signal: event.candidate,
                    type: 'candidate',
                });
            }
        };
        pc.ontrack = (event) => {
            const track = event.track;
            const peer = this.peerMetadata.get(remoteSocketId);
            const peerUid = peer?.userId || remoteSocketId;
            console.log(`[MeciVoice] Received remote track (${track.kind}, id=${track.id}) from ${peerUid}`);
            if (track.kind === 'audio') {
                // Ekran sesi mi yoksa mikrofon mu ayırt et
                const streamHasVideo = event.streams.some((s) => s.getVideoTracks().length > 0);
                const isScreenAudio = streamHasVideo ||
                    track.contentHint === 'music' ||
                    (this.remoteAudioElements.has(remoteSocketId) && (peer?.screenSharing || streamHasVideo));
                if (isScreenAudio) {
                    console.log(`[MeciVoice 📺🔊] Routing track to DEDICATED SCREEN/STREAM AUDIO for ${peerUid}`);
                    let streamAudioEl = this.remoteStreamAudioElements.get(remoteSocketId);
                    if (!streamAudioEl) {
                        streamAudioEl = document.createElement('audio');
                        streamAudioEl.autoplay = true;
                        streamAudioEl.style.display = 'none';
                        document.body.appendChild(streamAudioEl);
                        this.remoteStreamAudioElements.set(remoteSocketId, streamAudioEl);
                        const outputDeviceId = localStorage.getItem('meci_voice_output_device');
                        if (outputDeviceId && outputDeviceId !== 'default' && typeof streamAudioEl.setSinkId === 'function') {
                            streamAudioEl.setSinkId(outputDeviceId).catch(console.warn);
                        }
                    }
                    this.setupRemoteStreamAudioGain(remoteSocketId, track, streamAudioEl);
                    track.onended = () => {
                        console.log(`[MeciVoice 📺🔇] Screen audio track ended for ${peerUid}`);
                        const el = this.remoteStreamAudioElements.get(remoteSocketId);
                        if (el) {
                            el.pause();
                            el.srcObject = null;
                            el.remove();
                            this.remoteStreamAudioElements.delete(remoteSocketId);
                        }
                        const ctx = this.remoteStreamAudioContexts.get(remoteSocketId);
                        if (ctx) {
                            try {
                                ctx.close();
                            }
                            catch { }
                            this.remoteStreamAudioContexts.delete(remoteSocketId);
                        }
                        this.remoteStreamGainNodes.delete(remoteSocketId);
                    };
                }
                else {
                    console.log(`[MeciVoice 🎙️] Routing track to USER MICROPHONE for ${peerUid}`);
                    let audioEl = this.remoteAudioElements.get(remoteSocketId);
                    if (!audioEl) {
                        audioEl = document.createElement('audio');
                        audioEl.autoplay = true;
                        audioEl.style.display = 'none';
                        document.body.appendChild(audioEl);
                        this.remoteAudioElements.set(remoteSocketId, audioEl);
                        const outputDeviceId = localStorage.getItem('meci_voice_output_device');
                        if (outputDeviceId && outputDeviceId !== 'default' && typeof audioEl.setSinkId === 'function') {
                            audioEl.setSinkId(outputDeviceId).catch(console.warn);
                        }
                    }
                    this.setupRemoteAudioGain(remoteSocketId, track, audioEl);
                }
            }
            else if (track.kind === 'video') {
                this.remoteVideoTracks.set(remoteSocketId, track);
                if (peer?.userId) {
                    this.remoteVideoTracks.set(String(peer.userId), track);
                }
                track.onended = () => {
                    this.remoteVideoTracks.delete(remoteSocketId);
                    this.emit('user-unpublished', { uid: peerUid, mediaType: 'video' });
                };
                track.onunmute = () => {
                    console.log(`[MeciVoice] Video track unmuted from ${peerUid}`);
                    const wrapper = this.createVideoTrackWrapper(track);
                    this.emit('user-published', {
                        user: { uid: peerUid },
                        mediaType: 'video',
                        videoTrack: wrapper,
                    });
                };
                const videoTrackWrapper = this.createVideoTrackWrapper(track);
                this.emit('user-published', {
                    user: { uid: peerUid },
                    mediaType: 'video',
                    videoTrack: videoTrackWrapper,
                });
            }
        };
        pc.onconnectionstatechange = () => {
            if (pc.connectionState === 'connected') {
                this.emit('connection-state-change', { curState: 'CONNECTED' });
            }
            else if (pc.connectionState === 'failed') {
                console.warn(`[MeciVoice] Peer connection failed for ${remoteSocketId}, attempting ICE restart...`);
                try {
                    if (typeof pc.restartIce === 'function') {
                        pc.restartIce();
                    }
                }
                catch (e) { }
            }
            else if (pc.connectionState === 'disconnected') {
                console.warn(`[MeciVoice] Peer connection disconnected for ${remoteSocketId}`);
            }
        };
        if (isInitiator) {
            try {
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                RealtimeService_1.realtimeService.getSocket().emit('voice:signal', {
                    toSocketId: remoteSocketId,
                    signal: offer,
                    type: 'offer',
                });
            }
            catch (err) {
                console.error('[MeciVoice] Error creating offer:', err);
            }
        }
        return pc;
    }
    closePeer(remoteSocketId) {
        const pc = this.peerConnections.get(remoteSocketId);
        if (pc) {
            pc.close();
            this.peerConnections.delete(remoteSocketId);
        }
        const audioEl = this.remoteAudioElements.get(remoteSocketId);
        if (audioEl) {
            audioEl.pause();
            audioEl.srcObject = null;
            audioEl.remove();
            this.remoteAudioElements.delete(remoteSocketId);
        }
        const streamAudioEl = this.remoteStreamAudioElements.get(remoteSocketId);
        if (streamAudioEl) {
            streamAudioEl.pause();
            streamAudioEl.srcObject = null;
            streamAudioEl.remove();
            this.remoteStreamAudioElements.delete(remoteSocketId);
        }
        const ctx = this.remoteAudioContexts.get(remoteSocketId);
        if (ctx) {
            try {
                ctx.close();
            }
            catch { }
            this.remoteAudioContexts.delete(remoteSocketId);
        }
        const sCtx = this.remoteStreamAudioContexts.get(remoteSocketId);
        if (sCtx) {
            try {
                sCtx.close();
            }
            catch { }
            this.remoteStreamAudioContexts.delete(remoteSocketId);
        }
        this.remoteGainNodes.delete(remoteSocketId);
        this.remoteStreamGainNodes.delete(remoteSocketId);
        this.remoteVolumes.delete(remoteSocketId);
        this.remoteStreamVolumes.delete(remoteSocketId);
        this.remoteStreamMuted.delete(remoteSocketId);
        this.screenAudioSenders.delete(remoteSocketId);
        this.remoteVideoTracks.delete(remoteSocketId);
    }
    // ── Broadcast Video Track to all Peers ──────────────────────────
    async broadcastVideoTrack(track, stream) {
        if (track) {
            try {
                track.contentHint = this.isScreenSharing ? 'detail' : 'motion';
            }
            catch (e) { }
        }
        for (const [remoteSocketId, pc] of this.peerConnections.entries()) {
            try {
                const senders = pc.getSenders();
                let videoSender = senders.find((s) => s.track && s.track.kind === 'video') ||
                    senders.find((s) => s.track === null);
                if (videoSender) {
                    await videoSender.replaceTrack(track);
                }
                else if (track && stream) {
                    videoSender = pc.addTrack(track, stream);
                }
                // Ensure transceiver is set to sendrecv when sharing video, or recvonly when stopped
                const transceivers = pc.getTransceivers();
                const videoTransceiver = transceivers.find((t) => t.sender === videoSender || t.receiver?.track?.kind === 'video');
                if (videoTransceiver) {
                    videoTransceiver.direction = track ? 'sendrecv' : 'recvonly';
                }
                // Always create and exchange offer so receiver gets track immediately without black screen
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                RealtimeService_1.realtimeService.getSocket().emit('voice:signal', {
                    toSocketId: remoteSocketId,
                    signal: offer,
                    type: 'offer',
                });
            }
            catch (err) {
                console.warn(`[MeciVoice] Error replacing/renegotiating video track for peer ${remoteSocketId}:`, err);
            }
        }
    }
    // ── Local Audio & VAD ───────────────────────────────────────────
    async setupLocalAudio() {
        try {
            const inputDeviceId = localStorage.getItem('meci_voice_input_device');
            const noiseSuppression = (0, realtimeConfig_1.getNoiseSuppressionEnabled)();
            const noiseEngine = (0, realtimeConfig_1.getNoiseEngine)();
            const echoCancellation = localStorage.getItem('meci_voice_echo_cancellation') !== 'false';
            const audioConstraints = {
                noiseSuppression: noiseEngine === 'webrtc' ? noiseSuppression : false,
                echoCancellation,
                autoGainControl: true,
            };
            if (inputDeviceId && inputDeviceId !== 'default') {
                audioConstraints.deviceId = { exact: inputDeviceId };
            }
            this.rawMicStream = await navigator.mediaDevices.getUserMedia({
                audio: audioConstraints,
                video: false,
            });
            this.localAudioStream = this.applyNoiseProcessor(this.rawMicStream, noiseSuppression, noiseEngine);
            const audioTrack = this.localAudioStream.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !this.isMuted;
            }
            this.startVAD();
        }
        catch (err) {
            console.warn('[MeciVoice] Could not acquire local microphone:', err);
        }
    }
    startVAD() {
        if (!this.localAudioStream)
            return;
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx)
                return;
            this.audioContext = new AudioCtx();
            const source = this.audioContext.createMediaStreamSource(this.localAudioStream);
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 256;
            source.connect(this.analyser);
            const bufferLength = this.analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
            this.vadInterval = setInterval(() => {
                if (!this.analyser || this.isMuted || !this.channelId) {
                    if (this.isCurrentlySpeaking) {
                        this.isCurrentlySpeaking = false;
                        this.notifySpeaking(false);
                    }
                    return;
                }
                this.analyser.getByteFrequencyData(dataArray);
                let sum = 0;
                for (let i = 0; i < bufferLength; i++) {
                    sum += dataArray[i];
                }
                const avg = sum / bufferLength;
                const speaking = avg > 8; // threshold
                if (speaking !== this.isCurrentlySpeaking) {
                    this.isCurrentlySpeaking = speaking;
                    this.notifySpeaking(speaking);
                }
            }, 100);
        }
        catch (e) {
            console.warn('[MeciVoice] Failed to start VAD:', e);
        }
    }
    notifySpeaking(speaking) {
        if (!this.uid || !this.channelId)
            return;
        this.emit('volume-indicator', { uid: this.uid, level: speaking ? 15 : 0 });
        RealtimeService_1.realtimeService.getSocket().emit('voice:speaking', {
            channelId: this.channelId,
            speaking,
        });
    }
    stopVAD() {
        if (this.vadInterval) {
            clearInterval(this.vadInterval);
            this.vadInterval = null;
        }
        if (this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close().catch(() => { });
            this.audioContext = null;
        }
        this.analyser = null;
        this.isCurrentlySpeaking = false;
    }
    // ── Dedicated Screen Audio Broadcast & Web Audio Graphs ─────────
    async broadcastScreenAudioTrack(track, stream) {
        for (const [remoteSocketId, pc] of this.peerConnections.entries()) {
            try {
                if (track && stream) {
                    let sender = this.screenAudioSenders.get(remoteSocketId);
                    if (sender) {
                        await sender.replaceTrack(track);
                    }
                    else {
                        sender = pc.addTrack(track, stream);
                        this.screenAudioSenders.set(remoteSocketId, sender);
                    }
                }
                else {
                    const sender = this.screenAudioSenders.get(remoteSocketId);
                    if (sender) {
                        try {
                            pc.removeTrack(sender);
                        }
                        catch { }
                    }
                    this.screenAudioSenders.delete(remoteSocketId);
                }
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                RealtimeService_1.realtimeService.getSocket().emit('voice:signal', {
                    toSocketId: remoteSocketId,
                    signal: offer,
                    type: 'offer',
                });
            }
            catch (err) {
                console.warn(`[MeciVoice] Error broadcasting screen audio to ${remoteSocketId}:`, err);
            }
        }
    }
    /** Web Audio Gain Graph: Kullanıcı Mikrofon Sesi (Kişinin sesi - 0 ila %200 ses ayarı) */
    setupRemoteAudioGain(socketId, track, audioEl) {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) {
                audioEl.srcObject = new MediaStream([track]);
                audioEl.volume = this.isDeafened ? 0 : 1;
                return;
            }
            const prevCtx = this.remoteAudioContexts.get(socketId);
            if (prevCtx) {
                try {
                    prevCtx.close();
                }
                catch { }
            }
            const ctx = new AudioCtx();
            if (ctx.state === 'suspended') {
                ctx.resume().catch(() => { });
            }
            const stream = new MediaStream([track]);
            const source = ctx.createMediaStreamSource(stream);
            const gainNode = ctx.createGain();
            const savedVol = this.remoteVolumes.get(socketId) ?? 100;
            // volume 100 -> gain 1.0; volume 200 -> gain 2.0 (+6dB power boost)
            gainNode.gain.value = this.isDeafened ? 0 : (savedVol / 100);
            source.connect(gainNode);
            gainNode.connect(ctx.destination);
            this.remoteAudioContexts.set(socketId, ctx);
            this.remoteGainNodes.set(socketId, gainNode);
            // Keep audioEl silent so it doesn't double-play
            audioEl.srcObject = stream;
            audioEl.muted = true;
            audioEl.play().catch(() => { });
        }
        catch (e) {
            console.warn('[MeciVoice] Web Audio remote mic gain setup fallback:', e);
            audioEl.srcObject = new MediaStream([track]);
            audioEl.muted = false;
            audioEl.volume = this.isDeafened ? 0 : 1;
        }
    }
    /** Web Audio Gain Graph: Ekran / Sistem / Yayın Sesi (Oyun ve video sesi - 0 ila %200 ses ayarı, bozulma önleyici kompresörlü) */
    setupRemoteStreamAudioGain(socketId, track, audioEl) {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) {
                audioEl.srcObject = new MediaStream([track]);
                audioEl.muted = false;
                audioEl.volume = this.isDeafened ? 0 : 1;
                return;
            }
            const prevCtx = this.remoteStreamAudioContexts.get(socketId);
            if (prevCtx) {
                try {
                    prevCtx.close();
                }
                catch { }
            }
            const ctx = new AudioCtx();
            if (ctx.state === 'suspended') {
                ctx.resume().catch(() => { });
            }
            const stream = new MediaStream([track]);
            const source = ctx.createMediaStreamSource(stream);
            const gainNode = ctx.createGain();
            // Dynamics compressor prevents distortion and clipping even at 200% volume
            const compressor = ctx.createDynamicsCompressor();
            compressor.threshold.setValueAtTime(-18, ctx.currentTime);
            compressor.knee.setValueAtTime(20, ctx.currentTime);
            compressor.ratio.setValueAtTime(4, ctx.currentTime);
            compressor.attack.setValueAtTime(0.003, ctx.currentTime);
            compressor.release.setValueAtTime(0.25, ctx.currentTime);
            const savedVol = this.remoteStreamVolumes.get(socketId) ?? 100;
            const isMuted = this.remoteStreamMuted.get(socketId) || this.isDeafened;
            // gain 1.0 = 100%, gain 2.0 = 200%
            gainNode.gain.value = isMuted ? 0 : (savedVol / 100);
            source.connect(gainNode);
            gainNode.connect(compressor);
            compressor.connect(ctx.destination);
            this.remoteStreamAudioContexts.set(socketId, ctx);
            this.remoteStreamGainNodes.set(socketId, gainNode);
            // Keep audioEl silent so it doesn't double-play
            audioEl.srcObject = stream;
            audioEl.muted = true;
            audioEl.play().catch(() => { });
            console.log(`[MeciVoice 📺🔊] Stream Gain Graph active: vol=${savedVol}%, isMuted=${isMuted}`);
        }
        catch (e) {
            console.warn('[MeciVoice] Web Audio remote stream gain setup fallback:', e);
            audioEl.srcObject = new MediaStream([track]);
            audioEl.muted = false;
            const isMuted = this.remoteStreamMuted.get(socketId) || this.isDeafened;
            audioEl.volume = isMuted ? 0 : 1;
        }
    }
    // ── Public API Methods ──────────────────────────────────────────
    async joinChannel(channelId, uid, options) {
        this.channelId = String(channelId);
        this.uid = String(uid);
        this.currentUserId = String(uid);
        this.displayName = options?.displayName || `User #${uid}`;
        this.avatar = options?.avatar || '';
        console.log(`[MeciVoice] Joining channel ${channelId} as UID ${uid}`);
        // Ensure socket connected
        const socket = RealtimeService_1.realtimeService.getSocket();
        if (!socket.connected) {
            socket.connect();
        }
        // Register active user ID
        socket.emit('user:register', { userId: this.uid });
        // Acquire microphone
        await this.setupLocalAudio();
        if (options?.cameraEnabled) {
            await this.toggleVideo(true);
        }
        // Emit join to realtime voice server
        socket.emit('voice:join', {
            channelId: this.channelId,
            userId: this.uid,
            displayName: this.displayName,
            avatar: this.avatar,
            muted: this.isMuted,
            deafened: this.isDeafened,
            cameraEnabled: this.isVideoEnabled,
            screenSharing: this.isScreenSharing,
        });
        this.emit('connection-state-change', { curState: 'CONNECTED' });
        this.netMonitor.start();
    }
    async leaveChannel() {
        if (!this.channelId)
            return;
        console.log(`[MeciVoice] Leaving channel ${this.channelId}`);
        this.netMonitor.stop();
        // Stop screen audio broadcast
        await this.broadcastScreenAudioTrack(null, null);
        this.screenAudioSenders.clear();
        // Notify server
        RealtimeService_1.realtimeService.getSocket().emit('voice:leave', {
            channelId: this.channelId,
        });
        // Close all peer connections
        this.peerConnections.forEach((pc) => pc.close());
        this.peerConnections.clear();
        // Clean up mic audio elements & contexts
        this.remoteAudioElements.forEach((el) => {
            el.pause();
            el.srcObject = null;
            el.remove();
        });
        this.remoteAudioElements.clear();
        // Clean up stream audio elements & contexts
        this.remoteStreamAudioElements.forEach((el) => {
            el.pause();
            el.srcObject = null;
            el.remove();
        });
        this.remoteStreamAudioElements.clear();
        this.remoteAudioContexts.forEach((ctx) => {
            try {
                ctx.close();
            }
            catch { }
        });
        this.remoteAudioContexts.clear();
        this.remoteStreamAudioContexts.forEach((ctx) => {
            try {
                ctx.close();
            }
            catch { }
        });
        this.remoteStreamAudioContexts.clear();
        this.remoteGainNodes.clear();
        this.remoteStreamGainNodes.clear();
        this.remoteVolumes.clear();
        this.remoteStreamVolumes.clear();
        this.remoteStreamMuted.clear();
        this.remoteVideoTracks.clear();
        this.peerMetadata.clear();
        // Stop local tracks & audio noise processors
        this.cleanupAudioProcessors();
        if (this.rawMicStream) {
            this.rawMicStream.getTracks().forEach((t) => t.stop());
            this.rawMicStream = null;
        }
        if (this.localAudioStream) {
            this.localAudioStream.getTracks().forEach((t) => t.stop());
            this.localAudioStream = null;
        }
        if (this.localVideoStream) {
            this.localVideoStream.getTracks().forEach((t) => t.stop());
            this.localVideoStream = null;
        }
        if (this.localScreenStream) {
            this.localScreenStream.getTracks().forEach((t) => t.stop());
            this.localScreenStream = null;
        }
        this.stopVAD();
        this.channelId = null;
        this.uid = null;
        this.currentUserId = null;
        this.isVideoEnabled = false;
        this.isScreenSharing = false;
        this.emit('connection-state-change', { curState: 'DISCONNECTED' });
    }
    cleanupAudioProcessors() {
        KrusAudioProcessor_1.krusAudioProcessor.cleanup();
        KrispAudioProcessor_1.krispAudioProcessor.cleanup();
        RNNoiseAudioProcessor_1.rnnoiseAudioProcessor.cleanup();
    }
    applyNoiseProcessor(rawStream, enabled, engine) {
        this.cleanupAudioProcessors();
        if (!enabled)
            return rawStream;
        switch (engine) {
            case 'krisp':
                return KrispAudioProcessor_1.krispAudioProcessor.processStream(rawStream);
            case 'krus':
                return KrusAudioProcessor_1.krusAudioProcessor.processStream(rawStream);
            case 'rnnoise':
                return RNNoiseAudioProcessor_1.rnnoiseAudioProcessor.processStream(rawStream);
            case 'webrtc':
            default:
                return rawStream;
        }
    }
    async setNoiseSuppression(enabled, engine) {
        const activeEngine = engine || (0, realtimeConfig_1.getNoiseEngine)();
        if (!this.rawMicStream)
            return;
        try {
            this.localAudioStream = this.applyNoiseProcessor(this.rawMicStream, enabled, activeEngine);
            const audioTrack = this.localAudioStream?.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !this.isMuted;
                for (const pc of this.peerConnections.values()) {
                    const senders = pc.getSenders();
                    const audioSender = senders.find((s) => s.track && s.track.kind === 'audio');
                    if (audioSender) {
                        await audioSender.replaceTrack(audioTrack);
                    }
                }
            }
            this.stopVAD();
            this.startVAD();
            this.emit('noise-suppression-changed', { enabled, engine: activeEngine });
            console.log(`[MeciVoice] Noise suppression switched: enabled=${enabled}, engine=${activeEngine}`);
        }
        catch (err) {
            console.error('[MeciVoice] Failed to update noise suppression:', err);
        }
    }
    async toggleMute(muted) {
        this.isMuted = muted;
        if (this.rawMicStream) {
            this.rawMicStream.getAudioTracks().forEach((t) => {
                t.enabled = !muted;
            });
        }
        if (this.localAudioStream) {
            this.localAudioStream.getAudioTracks().forEach((t) => {
                t.enabled = !muted;
            });
        }
        if (this.channelId) {
            RealtimeService_1.realtimeService.getSocket().emit('voice:state-change', {
                channelId: this.channelId,
                muted,
            });
        }
    }
    async toggleDeafen(deafened) {
        this.isDeafened = deafened;
        // Kullanıcı konuşma sesleri (Mikrofon)
        for (const [sId, gainNode] of this.remoteGainNodes.entries()) {
            const vol = this.remoteVolumes.get(sId) ?? 100;
            gainNode.gain.value = deafened ? 0 : (vol / 100);
        }
        this.remoteAudioElements.forEach((el) => {
            el.volume = deafened ? 0 : 1;
        });
        // Ekran / Sistem sesleri (Yayın sesi)
        for (const [sId, gainNode] of this.remoteStreamGainNodes.entries()) {
            const vol = this.remoteStreamVolumes.get(sId) ?? 100;
            const isMuted = this.remoteStreamMuted.get(sId) || deafened;
            gainNode.gain.value = isMuted ? 0 : (vol / 100);
        }
        this.remoteStreamAudioElements.forEach((el) => {
            el.volume = deafened ? 0 : 1;
        });
        if (deafened) {
            await this.toggleMute(true);
        }
        if (this.channelId) {
            RealtimeService_1.realtimeService.getSocket().emit('voice:state-change', {
                channelId: this.channelId,
                deafened,
            });
        }
    }
    async setCamera(deviceId) {
        localStorage.setItem('meci_voice_camera_device', deviceId);
        console.log(`[MeciVoice] Selected camera device: ${deviceId}`);
        if (this.isVideoEnabled && this.localVideoStream) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        deviceId: deviceId === 'default' ? undefined : { exact: deviceId },
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        frameRate: { ideal: 30 },
                    },
                    audio: false,
                });
                const oldTrack = this.localVideoStream.getVideoTracks()[0];
                if (oldTrack)
                    oldTrack.stop();
                this.localVideoStream = stream;
                const newTrack = stream.getVideoTracks()[0];
                if (newTrack) {
                    newTrack.enabled = true;
                    if (!this.isScreenSharing) {
                        await this.broadcastVideoTrack(newTrack, this.localVideoStream);
                    }
                    const wrapper = this.createVideoTrackWrapper(newTrack, true);
                    this.emit('local-tracks', {
                        audioTrack: null,
                        videoTrack: wrapper,
                    });
                }
            }
            catch (err) {
                console.error('[MeciVoice] Failed to switch camera device on live stream:', err);
            }
        }
    }
    async setMicrophone(deviceId) {
        localStorage.setItem('meci_voice_input_device', deviceId);
        console.log(`[MeciVoice] Selected microphone: ${deviceId}`);
        if (this.channelId) {
            if (this.rawMicStream) {
                this.rawMicStream.getTracks().forEach((t) => t.stop());
                this.rawMicStream = null;
            }
            await this.setupLocalAudio();
            const newTrack = this.localAudioStream?.getAudioTracks()[0];
            if (newTrack) {
                newTrack.enabled = !this.isMuted;
                for (const pc of this.peerConnections.values()) {
                    const senders = pc.getSenders();
                    const audioSender = senders.find((s) => s.track && s.track.kind === 'audio');
                    if (audioSender) {
                        await audioSender.replaceTrack(newTrack);
                    }
                }
            }
        }
    }
    async setOutputDevice(deviceId) {
        localStorage.setItem('meci_voice_output_device', deviceId);
        console.log(`[MeciVoice] Selected speaker/headphones: ${deviceId}`);
        const sinkId = deviceId === 'default' ? '' : deviceId;
        for (const audioEl of this.remoteAudioElements.values()) {
            try {
                if (typeof audioEl.setSinkId === 'function') {
                    await audioEl.setSinkId(sinkId);
                }
            }
            catch (err) {
                console.warn('[MeciVoice] setSinkId error:', err);
            }
        }
        for (const audioEl of this.remoteStreamAudioElements.values()) {
            try {
                if (typeof audioEl.setSinkId === 'function') {
                    await audioEl.setSinkId(sinkId);
                }
            }
            catch (err) {
                console.warn('[MeciVoice] setSinkId error for stream audio:', err);
            }
        }
    }
    async toggleVideo(cameraEnabled) {
        this.isVideoEnabled = cameraEnabled;
        if (cameraEnabled) {
            try {
                const cameraDeviceId = localStorage.getItem('meci_voice_camera_device');
                const videoConstraints = {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    frameRate: { ideal: 30 },
                };
                if (cameraDeviceId && cameraDeviceId !== 'default') {
                    videoConstraints.deviceId = { exact: cameraDeviceId };
                }
                try {
                    this.localVideoStream = await navigator.mediaDevices.getUserMedia({
                        video: videoConstraints,
                        audio: false,
                    });
                }
                catch (hdErr) {
                    console.warn('[MeciVoice] HD camera failed, trying standard camera:', hdErr);
                    this.localVideoStream = await navigator.mediaDevices.getUserMedia({
                        video: cameraDeviceId && cameraDeviceId !== 'default' ? { deviceId: { exact: cameraDeviceId } } : true,
                        audio: false,
                    });
                }
                const videoTrack = this.localVideoStream.getVideoTracks()[0];
                if (videoTrack) {
                    videoTrack.enabled = true;
                }
                // Screen share takes priority if already running; otherwise broadcast camera
                if (!this.isScreenSharing) {
                    await this.broadcastVideoTrack(videoTrack, this.localVideoStream);
                }
                const localTrackWrapper = this.createVideoTrackWrapper(videoTrack, true);
                this.emit('local-tracks', {
                    audioTrack: null,
                    videoTrack: localTrackWrapper,
                });
            }
            catch (err) {
                console.error('[MeciVoice] Camera access failed completely:', err);
                this.isVideoEnabled = false;
                return;
            }
        }
        else {
            if (this.localVideoStream) {
                this.localVideoStream.getTracks().forEach((t) => t.stop());
                this.localVideoStream = null;
            }
            // If screen share is not active, clear video track for peers
            if (!this.isScreenSharing) {
                await this.broadcastVideoTrack(null, null);
                this.emit('local-tracks', { audioTrack: null, videoTrack: null });
            }
        }
        if (this.channelId) {
            RealtimeService_1.realtimeService.getSocket().emit('voice:state-change', {
                channelId: this.channelId,
                cameraEnabled: this.isVideoEnabled,
                screenSharing: this.isScreenSharing,
            });
        }
    }
    async toggleScreenShare(screenSharing, sourceId, withAudio = true) {
        this.isScreenSharing = screenSharing;
        if (screenSharing) {
            try {
                let stream;
                const isElectron = typeof window !== 'undefined' && window.ipcRenderer;
                if (isElectron && sourceId) {
                    stream = await navigator.mediaDevices.getUserMedia({
                        audio: withAudio ? {
                            mandatory: {
                                chromeMediaSource: 'desktop',
                            }
                        } : false,
                        video: {
                            mandatory: {
                                chromeMediaSource: 'desktop',
                                chromeMediaSourceId: sourceId,
                                minWidth: 1280,
                                maxWidth: 1920,
                                minHeight: 720,
                                maxHeight: 1080,
                            },
                        },
                    });
                }
                else if (navigator.mediaDevices.getDisplayMedia) {
                    const isFirefoxOrZen = typeof navigator !== 'undefined' && (/firefox|zen/i.test(navigator.userAgent) ||
                        !(('systemAudio' in (navigator.mediaDevices.getSupportedConstraints?.() || {}))));
                    if (isFirefoxOrZen) {
                        console.log('[MeciVoice 🦊] Zen Browser / Firefox detected. Using Gecko-compliant displayMedia options for audio capture...');
                        try {
                            stream = await navigator.mediaDevices.getDisplayMedia({
                                video: true,
                                audio: withAudio ? true : false,
                            });
                        }
                        catch (zenErr) {
                            console.warn('[MeciVoice] Zen displayMedia with audio failed, retrying without audio:', zenErr);
                            stream = await navigator.mediaDevices.getDisplayMedia({
                                video: true,
                                audio: false,
                            });
                        }
                    }
                    else {
                        // Chromium (Chrome, Edge, Brave, Opera, Arc) standard
                        const displayMediaOptions = {
                            video: {
                                cursor: 'always',
                                displaySurface: 'monitor',
                            },
                            audio: withAudio ? {
                                echoCancellation: false,
                                noiseSuppression: false,
                                autoGainControl: false,
                                suppressLocalAudioPlayback: false,
                            } : false,
                            systemAudio: withAudio ? 'include' : 'exclude',
                            surfaceSwitching: 'include',
                            selfBrowserSurface: 'exclude',
                            preferCurrentTab: false,
                        };
                        try {
                            stream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);
                        }
                        catch (chromErr) {
                            console.warn('[MeciVoice] Advanced getDisplayMedia options failed, retrying with standard fallback:', chromErr);
                            stream = await navigator.mediaDevices.getDisplayMedia({
                                video: true,
                                audio: withAudio ? true : false,
                            });
                        }
                    }
                }
                else {
                    throw new Error('Screen share not supported in this environment');
                }
                this.localScreenStream = stream;
                const screenTrack = stream.getVideoTracks()[0];
                // Screen audio track (Yayının sistem sesi) - Mikrofondan tamamen bağımsız ayrı kanal olarak yayınlanır
                const screenAudioTracks = stream.getAudioTracks();
                if (screenAudioTracks.length > 0) {
                    const screenAudioTrack = screenAudioTracks[0];
                    screenAudioTrack.contentHint = 'music';
                    console.log('[MeciVoice 📺🔊] Screen audio captured! Broadcasting dedicated screen audio track to peers...');
                    await this.broadcastScreenAudioTrack(screenAudioTrack, stream);
                }
                screenTrack.onended = () => {
                    this.toggleScreenShare(false);
                };
                // Broadcast screen share track to peers
                await this.broadcastVideoTrack(screenTrack, stream);
                const localTrackWrapper = this.createVideoTrackWrapper(screenTrack, true);
                this.emit('local-tracks', {
                    audioTrack: null,
                    videoTrack: localTrackWrapper,
                });
                this.emit('screen-share-changed', true);
            }
            catch (err) {
                console.error('[MeciVoice] Screen share failed:', err);
                this.isScreenSharing = false;
                return;
            }
        }
        else {
            await this.broadcastScreenAudioTrack(null, null);
            if (this.localScreenStream) {
                this.localScreenStream.getTracks().forEach((t) => t.stop());
                this.localScreenStream = null;
            }
            // If camera was active, revert back to camera stream
            if (this.isVideoEnabled && this.localVideoStream) {
                const camTrack = this.localVideoStream.getVideoTracks()[0];
                await this.broadcastVideoTrack(camTrack, this.localVideoStream);
                this.emit('local-tracks', {
                    audioTrack: null,
                    videoTrack: this.createVideoTrackWrapper(camTrack, true),
                });
            }
            else {
                await this.broadcastVideoTrack(null, null);
                this.emit('local-tracks', { audioTrack: null, videoTrack: null });
            }
            this.emit('screen-share-changed', false);
        }
        if (this.channelId) {
            RealtimeService_1.realtimeService.getSocket().emit('voice:state-change', {
                channelId: this.channelId,
                cameraEnabled: this.isVideoEnabled,
                screenSharing: this.isScreenSharing,
            });
        }
    }
    /**
     * Kullanıcı Konuşma Sesi (Mikrofon): 0 ila %200 ses ayarı.
     * Yalnızca kişinin sesini etkiler, yayın sesine dokunmaz!
     */
    setRemoteVolume(uidStr, volume) {
        try {
            const clampedVol = Math.max(0, Math.min(200, volume));
            for (const [sId, meta] of this.peerMetadata.entries()) {
                if (meta.userId === uidStr || sId === uidStr) {
                    this.remoteVolumes.set(sId, clampedVol);
                    const gainNode = this.remoteGainNodes.get(sId);
                    const ctx = this.remoteAudioContexts.get(sId);
                    if (ctx && ctx.state === 'suspended') {
                        ctx.resume().catch(() => { });
                    }
                    if (gainNode) {
                        // gain 1.0 = 100%, gain 2.0 = 200% (+6dB power amplification)
                        const gainVal = this.isDeafened ? 0 : (clampedVol / 100);
                        gainNode.gain.setValueAtTime(gainVal, ctx?.currentTime || 0);
                    }
                    else {
                        const audioEl = this.remoteAudioElements.get(sId);
                        if (audioEl) {
                            audioEl.volume = this.isDeafened ? 0 : Math.min(1, clampedVol / 100);
                        }
                    }
                }
            }
        }
        catch (e) {
            console.warn('[MeciVoice] setRemoteVolume error:', e);
        }
    }
    /**
     * Yayın Sesi (Ekran / Sistem Sesi): 0 ila %200 ses ayarı.
     * Yalnızca ekranın oyun/video sistem sesini açıp kapatır ve güçlendirir.
     * Kişinin mikrofon konuşma sesini ASLA kapatmaz ve bozmaz!
     */
    setRemoteStreamVolume(uidStr, volume) {
        try {
            const clampedVol = Math.max(0, Math.min(200, volume));
            for (const [sId, meta] of this.peerMetadata.entries()) {
                if (meta.userId === uidStr || sId === uidStr) {
                    this.remoteStreamVolumes.set(sId, clampedVol);
                    const gainNode = this.remoteStreamGainNodes.get(sId);
                    const ctx = this.remoteStreamAudioContexts.get(sId);
                    if (ctx && ctx.state === 'suspended') {
                        ctx.resume().catch(() => { });
                    }
                    const isMuted = this.remoteStreamMuted.get(sId) || this.isDeafened;
                    if (gainNode) {
                        // gain 1.0 = 100%, gain 2.0 = 200% boost
                        gainNode.gain.setValueAtTime(isMuted ? 0 : (clampedVol / 100), ctx?.currentTime || 0);
                    }
                    else {
                        const audioEl = this.remoteStreamAudioElements.get(sId);
                        if (audioEl) {
                            audioEl.volume = isMuted ? 0 : Math.min(1, clampedVol / 100);
                        }
                    }
                }
            }
        }
        catch (e) {
            console.warn('[MeciVoice] setRemoteStreamVolume error:', e);
        }
    }
    setRemoteStreamMuted(uidStr, muted) {
        try {
            for (const [sId, meta] of this.peerMetadata.entries()) {
                if (meta.userId === uidStr || sId === uidStr) {
                    this.remoteStreamMuted.set(sId, muted);
                    const vol = this.remoteStreamVolumes.get(sId) ?? 100;
                    this.setRemoteStreamVolume(uidStr, muted ? 0 : vol);
                }
            }
        }
        catch (e) {
            console.warn('[MeciVoice] setRemoteStreamMuted error:', e);
        }
    }
    getManualQuality() {
        return this.manualQuality;
    }
    async setVideoQuality(qualityKey) {
        this.manualQuality = qualityKey;
        (0, realtimeConfig_1.setVideoQualitySelection)(qualityKey);
        console.log(`[MeciVoice 🎬] Applying video quality profile: ${qualityKey}`);
        if (qualityKey === 'auto') {
            return;
        }
        const q = realtimeConfig_1.VIDEO_QUALITY_PROFILES[qualityKey];
        if (!q)
            return;
        // Apply constraints to local publishing tracks
        const localTracks = [
            ...(this.localScreenStream ? this.localScreenStream.getVideoTracks() : []),
            ...(this.localVideoStream ? this.localVideoStream.getVideoTracks() : []),
        ];
        for (const track of localTracks) {
            try {
                await track.applyConstraints({
                    width: { ideal: q.width, max: q.width },
                    height: { ideal: q.height, max: q.height },
                    frameRate: { ideal: q.frameRate, max: q.frameRate },
                });
            }
            catch (err) {
                console.warn('[MeciVoice] applyConstraints error for quality:', qualityKey, err);
            }
        }
        // Apply bitrate and framerate parameters to all active WebRTC senders
        for (const pc of this.peerConnections.values()) {
            for (const sender of pc.getSenders()) {
                if (sender.track && sender.track.kind === 'video') {
                    try {
                        const params = sender.getParameters();
                        if (!params.encodings || params.encodings.length === 0) {
                            params.encodings = [{}];
                        }
                        params.encodings[0].maxBitrate = q.bitrateMax * 1000;
                        params.encodings[0].scaleResolutionDownBy = 1.0;
                        params.encodings[0].maxFramerate = q.frameRate;
                        await sender.setParameters(params);
                    }
                    catch (e) {
                        console.warn('[MeciVoice] setParameters quality error:', e);
                    }
                }
            }
        }
    }
    sendAdminModeration(targetSocketId, targetUserId, action, value) {
        console.log(`[MeciVoice 🛡️] Admin sending ${action} to user:${targetUserId || targetSocketId}`);
        if (targetSocketId) {
            RealtimeService_1.realtimeService.getSocket().emit('voice:signal', {
                toSocketId: targetSocketId,
                signal: { action, value, targetUserId, adminUid: this.uid },
                type: 'admin-moderation',
            });
        }
        RealtimeService_1.realtimeService.getSocket().emit('admin:voice-action', {
            targetSocketId,
            targetUserId,
            channelId: this.channelId,
            action,
            value,
        });
        if (action === 'mute') {
            this.setRemoteVolume(targetUserId, value ? 0 : 100);
            if (targetSocketId)
                this.setRemoteVolume(targetSocketId, value ? 0 : 100);
        }
    }
}
exports.MeciVoiceService = MeciVoiceService;
exports.meciVoiceService = new MeciVoiceService();
